import {
    Box,
    Button,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { X } from 'lucide-react';
import { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { useSetRecoilState } from 'recoil';
import ObliquePlaneMarker from '~/components/Annotations/ObliquePlaneMarker';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { AnnotationPlane } from '~/domain/annotation/annotation';
import { PointMm } from '~/domain/geometry/patientGeometry';
import { useResidentEnvironments } from '~/hooks/environment/useResidentEnvironments';
import { useImageForSeries } from '~/hooks/image/useImageForSeries';
import { useIsObliqueToImage } from '~/hooks/useAnnotationGoTo';
import { selectedAnnotationState } from '~/state/atoms/annotationListAtoms';
import { resolveSeriesLabel } from '~/utils/seriesLabel';
import { planePositionAlong } from '~/utils/units';
import { formatPlanePositionLine } from '~/viewportGrid';

/**
 * The title row of the selected shape's analysis: its kind, `readout`, a
 * close that deselects it and a go-to back to where it was placed, labelled
 * with where its plane lies as a tile reads it, e.g. "Z: 2.50 cm". A shape
 * still being placed, with no `id`, has neither: its plane's position is
 * plain text. It names the image unless that is the only dataset resident,
 * so figures from two scans cannot be confused.
 */
export default function AnalysisHeader({
    id,
    plane,
    anchorMm,
    titleId,
    readout,
    goTo,
}: {
    /** The placed shape's id; absent while it is being placed. */
    id?: string;
    plane: AnnotationPlane;
    /** A point of the shape, in its image's own frame. */
    anchorMm: PointMm;
    titleId: string;
    readout: ReactNode;
    goTo: (id: string) => void;
}) {
    const intl = useIntl();
    const setSelected = useSetRecoilState(selectedAnnotationState);
    const residents = useResidentEnvironments();
    const isOblique = useIsObliqueToImage()({ plane });
    const { seriesInstanceUid, viewDirection } = plane;
    const image = useImageForSeries()(seriesInstanceUid);
    const position = formatPlanePositionLine(
        planePositionAlong(anchorMm, viewDirection)
    );
    const series =
        residents.length === 1 &&
        residents[0].image?.parent.seriesUid === seriesInstanceUid
            ? undefined
            : resolveSeriesLabel(
                  seriesInstanceUid,
                  [image],
                  intl.formatMessage({ id: 'unknown.series' })
              );

    const goToLabel = intl.formatMessage({ id: 'measure.analysis.goTo' });
    const closeLabel = intl.formatMessage({ id: 'measure.analysis.close' });

    return (
        <Box sx={{ mb: 0.5 }}>
            <Stack
                direction="row"
                alignItems="center"
                sx={{ flexWrap: 'wrap', columnGap: 0.5, minHeight: 28 }}
            >
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                        {intl.formatMessage({ id: titleId })}
                    </Typography>
                    {isOblique && (
                        <ObliquePlaneMarker labelId="measure.analysis.oblique" />
                    )}
                    <Typography
                        component="span"
                        sx={{ fontSize: 12, color: 'text.disabled' }}
                    >
                        ·
                    </Typography>
                    {id === undefined ? (
                        <Typography
                            component="span"
                            sx={{
                                fontSize: 12,
                                color: 'text.secondary',
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {position}
                        </Typography>
                    ) : (
                        <Tooltip title={goToLabel} describeChild>
                            <Button
                                size="small"
                                onClick={() => goTo(id)}
                                sx={{
                                    minWidth: 0,
                                    px: 0.5,
                                    py: 0,
                                    fontSize: 12,
                                    lineHeight: 1.6,
                                    textTransform: 'none',
                                    fontVariantNumeric: 'tabular-nums',
                                }}
                            >
                                {position}
                            </Button>
                        </Tooltip>
                    )}
                </Stack>
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.25}
                    sx={{ ml: 'auto' }}
                >
                    <Typography
                        variant="caption"
                        data-testid="measure-analysis-readout"
                        sx={{
                            fontVariantNumeric: 'tabular-nums',
                            whiteSpace: 'nowrap',
                            color: 'text.primary',
                        }}
                    >
                        {readout}
                    </Typography>
                    {id !== undefined && (
                        <Tooltip title={closeLabel}>
                            <IconButton
                                size="small"
                                aria-label={closeLabel}
                                onClick={() => setSelected(null)}
                            >
                                <X size={14} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>
            </Stack>
            {series && (
                <Stack direction="row" alignItems="baseline" spacing={0.5}>
                    <Typography
                        variant="caption"
                        noWrap
                        sx={{ color: 'text.secondary' }}
                    >
                        <SensitiveInfoTypography>
                            {series.label}
                        </SensitiveInfoTypography>
                    </Typography>
                    {series.sublabel && (
                        <Typography
                            variant="caption"
                            sx={{ color: 'text.disabled', flexShrink: 0 }}
                        >
                            {series.sublabel}
                        </Typography>
                    )}
                </Stack>
            )}
        </Box>
    );
}
